/**
 * Career UI framing-text catalog (nav, buttons, auth, empty states — everything
 * not LLM-authored) (i18n-adr.md). English keys, English default text = the en
 * fallback; the `de-DE` overrides carry the German the user actually sees. ALL of
 * this framing text flows through `t()` — a hardcoded German literal in an element
 * or VM fails review (ADR I2 rule 2). Content prose (LLM-authored) is out of scope
 * here — it carries `PresentationMeta.locale` and renders `lang="de"`.
 */

import { createI18n, type I18n, type MessageValue } from "@platform/i18n"

/** English-default catalog — its keys are the typed surface of `t`. */
export const catalog = {
  "app.name": "Career",
  "app.tagline": "Find jobs that fit you. Generate applications that sound like you.",

  "nav.onboarding": "Onboarding",
  "nav.review": "Proposals",
  "nav.workshops": "Workshops",
  "nav.kompass": "Compass",
  "nav.suche": "Search",
  "nav.bewerbungen": "Applications",
  "nav.laeufe": "Runs",
  "nav.einstellungen": "AI settings",
  "nav.herzschlag": "Heartbeat",
  "nav.mehr": "More",
  "nav.profil": "Profile",
  "nav.lebenslauf": "CV",
  "nav.belege": "Evidence",
  "nav.nachweise": "Documents",
  "a11y.skipToContent": "Skip to content",

  "auth.login": "Sign in",
  "auth.logout": "Sign out",
  "auth.devLogin": "Dev sign-in",
  "auth.signedInAs": "Signed in as {name}",

  // ─── App-bar chrome (theme toggle + resource metadata) ────────
  "theme.label": "Color theme",
  "theme.light": "Light",
  "theme.dark": "Dark",
  "theme.auto": "Auto",
  "bar.nav.label": "Sections",
  "bar.resourcePath.label": "Resource path",
  "meta.open": "View details",
  "meta.title": "This view",
  "meta.resource": "View",
  "meta.owner": "Owner",
  "meta.visibility": "Visibility",
  "meta.sharing": "Shared with",
  "meta.visibility.private": "Private",
  "meta.sharing.none": "Only you",

  // ─── Top navbar: breadcrumb + user menu (§7a) ─────────────────
  "breadcrumb.label": "You are here",
  "breadcrumb.appSpace": "Switch area",
  "breadcrumb.step": "Jump to step",
  "user.menu.open": "Account menu",
  "user.menu.title": "Account",
  // Workspace = the user's organizing period (deferred feature — a default label for now).
  "workspace.default": "Standard workspace",
  // Workshop display names for the Sub-area crumb (mirror the server method-pack names).
  "breadcrumb.workshop.self-knowledge": "Workshop 1 · Self-image",
  "breadcrumb.workshop.search-profile": "Workshop 2 · Search profile",
  "breadcrumb.workshop.profiler": "Workshop 3 · Profiler",

  "login.title": "Sign in to Career",
  "login.body": "Career helps you find jobs that fit you \u2014 and write applications that sound like you. Sign in with your platform account.",
  "login.devHint": "Development mode: test session active (without Keycloak).",
  "login.cta": "Sign in with Platform account",
  "brand.name": "Career Studio",
  "nav.collapseSidebar": "Collapse sidebar",
  "nav.expandSidebar": "Expand sidebar",
  "aside.toggle": "Toggle details panel",
  "aside.title": "Details",
  "aside.empty": "Contextual details for the current view will appear here.",

  // ─── Left pane: within-space section nav (§7) ─────────────────
  "sectionnav.label": "Sections",
  "sectionnav.empty": "No sections in this area yet.",
  "sectionnav.overview": "Overview",
  "sectionnav.ws.self": "Self-image",
  "sectionnav.ws.search": "Search profile",
  "sectionnav.ws.profiler": "Profiler",
  "sectionnav.onboarding.upload": "Documents",
  "sectionnav.indicator.open": "{n} open",

  // ─── Right pane: Platform AI Agent ────────────────────────────
  "ai.panel.title": "Platform AI",
  "ai.panel.subtitle": "Help with everything you do on the platform.",
  "ai.panel.toggle": "Toggle the Platform AI agent",
  "ai.panel.short": "AI",
  "ai.panel.apps": "Your apps",
  "ai.panel.thisApp": "This app",
  "ai.panel.moreSoon": "More applications appear here as you gain access to them.",
  "ai.panel.composerPlaceholder": "Ask Platform AI …",
  "ai.panel.comingSoon": "Coming soon",

  "notfound.title": "Page not found",
  "notfound.body": "This route does not exist.",
  "notfound.back": "Back to search",

  "common.comingSoon": "This view is being built in a later phase. The route, shell, and data plane already work.",
  "gated.hint": "Available with the appropriate authorization.",

  "surface.onboarding.title": "Onboarding",
  "surface.extraction.title": "Review proposals",
  "surface.workshops.title": "Workshops",
  "surface.workshopRunner.title": "Workshop",
  "surface.kompass.title": "Compass",
  "surface.profil.title": "Profile",
  "surface.lebenslauf.title": "CV",
  "surface.belege.title": "Evidence ledger",
  "surface.nachweise.title": "Documents",
  "surface.suche.title": "Search",
  "surface.opportunity.title": "Opportunity",
  "surface.bewerbungen.title": "Applications",
  "surface.laeufe.title": "Runs",
  "surface.einstellungen.title": "AI settings",

  "heartbeat.title": "Push-plane heartbeat",
  "heartbeat.intro": "Triggers a scripted search run through the real api and store, and renders it live from the push plane.",
  "heartbeat.trigger": "Trigger heartbeat",
  "heartbeat.triggering": "Running …",
  "heartbeat.railTitle": "Search runs",
  "heartbeat.empty": "No runs yet. Trigger a heartbeat to see the live rail.",
  "heartbeat.stage.discovering": "Discovering",
  "heartbeat.stage.verifying": "Verifying",
  "heartbeat.stage.matching": "Matching",
  "heartbeat.stage.enriching": "Enriching",
  "heartbeat.stage.done": "Done",
  "heartbeat.status.running": "running",
  "heartbeat.status.completed": "completed",
  "heartbeat.status.stopped": "stopped",
  "heartbeat.status.failed": "failed",
  "heartbeat.count.discovered": "{n} discovered",
  "heartbeat.count.verified": "{n} verified",
  "heartbeat.count.matched": "{n} matched",
  "heartbeat.error": "Error: {message}",

  // ─── Onboarding ───────────────────────────────────────────────
  "onboarding.title": "Onboarding",
  "onboarding.lead": "Upload your documents. We recognise your strengths in them — you decide what counts.",
  "onboarding.kind.cv": "CV",
  "onboarding.kind.letter": "Cover letter",
  "onboarding.kind.reference": "Reference",
  "onboarding.kind.certificate": "Certificate",
  "onboarding.kind.portrait": "Photo",
  "onboarding.formatHint": "We read PDF, DOCX, Markdown (.md) and text (.txt).",
  "onboarding.upload": "Upload",
  "onboarding.uploading": "Uploading …",
  "onboarding.uploadedN": { one: "{n} file uploaded", other: "{n} files uploaded" },
  "onboarding.error": "Error: {message}",
  "onboarding.start": "Start analysis",
  "onboarding.starting": "Starting …",
  "onboarding.runLabel": "Analysis",
  "onboarding.status.pending": "Ready",
  "onboarding.status.extracting": "Analysing …",
  "onboarding.status.proposed": "Proposals ready",
  "onboarding.status.confirmed": "Confirmed",
  "onboarding.status.failed": "Failed",
  "onboarding.count.docs": { one: "{n} document", other: "{n} documents" },
  "onboarding.count.claims": { one: "{n} statement", other: "{n} statements" },
  "onboarding.runEmpty": "No analysis started yet.",
  "onboarding.zeroClaims": "We couldn't find any readable text. Scanned documents or image files often have no text layer — please upload a PDF with selectable text if you can.",
  "onboarding.toReview": "Review proposals",

  // ─── Extraction review ────────────────────────────────────────
  "review.title": "Review proposals",
  "review.lead": "Review what we read from your documents. Nothing counts as evidence until you confirm it.",
  "review.error": "Error: {message}",
  "review.grade.evidenced": "evidenced",
  "review.grade.selfPositioning": "self-positioning",
  "review.grade.gap": "gap",
  "review.sources": { one: "{n} source", other: "{n} sources" },
  "review.confirm": "Confirm",
  "review.confirmed": "Confirmed",
  "review.edit": "Edit",
  "review.save": "Save",
  "review.discard": "Discard",
  "review.editLabel": "Edit statement",
  "review.claims.title": "Statements",
  "review.claims.count": "{open} open · {confirmed} confirmed",
  "review.claims.empty": "No statements yet. Start the analysis in onboarding first.",
  "review.claims.emptyAfterRun": "We couldn't find any readable text. Scanned documents or image files often have no text layer — please upload a PDF with selectable text if you can.",
  "review.confirmIngest": "Finish review",
  "review.rederive": "Re-analyse",
  "review.rederiving": "Re-analysing …",
  "review.cv.title": "CV draft",
  "review.cv.name": "Name",
  "review.cv.stations": "Positions",
  "review.cv.stationsN": { one: "{n} position", other: "{n} positions" },
  "review.cv.skills": "Skills",
  "review.cv.skillsN": { one: "{n} skill", other: "{n} skills" },
  "review.cv.empty": "No CV draft yet.",
  "review.profile.title": "Profile seed",
  "review.profile.noThroughline": "No throughline yet.",
  "review.profile.motives": { one: "{m} motivation", other: "{m} motivations" },
  "review.profile.values": { one: "{v} value", other: "{v} values" },
  "review.profile.empty": "No profile seed yet.",
  "review.narrative.title": "Candidate profile",
  "review.narrative.provenance": { one: "{n} cited passage", other: "{n} cited passages" },
  "review.narrative.authoredOn": "Created on {date}",
  "review.narrative.stale": "Out of date — confirmed evidence has changed",
  "review.narrative.author": "Create candidate profile",
  "review.narrative.reauthor": "Re-create",
  "review.narrative.authoring": "Creating …",
  "review.narrative.empty": "Confirm evidence, and your candidate profile — a profile that sounds like you — appears here.",

  // ─── Compass hub ──────────────────────────────────────────────
  "compass.lead": "Everything at a glance: who you are, what you can do, and how you prove it.",
  "compass.profile.desc": "Your candidate profile and what drives you.",
  "compass.profile.ready": "ready",
  "compass.profile.pending": "not yet created",
  "compass.cv.desc": "Your CV — draft, versions, export, print.",
  "compass.cv.versions": { one: "{n} version", other: "{n} versions" },
  "compass.cv.draft": "draft open",
  "compass.evidence.desc": "Every graded statement you can back up.",
  "compass.evidence.confirmed": "{n} confirmed",
  "compass.evidence.open": "{n} open",
  "compass.assets.desc": "Your certificates, references and documents.",
  "compass.assets.count": { one: "{n} document", other: "{n} documents" },

  // ─── Profile ──────────────────────────────────────────────────
  "profile.lead": "This is how you come across — built from the evidence you confirmed.",
  "profile.empty": "Create your profile: start with onboarding and make your strengths visible with evidence.",
  "profile.identity.title": "Your throughline",
  "profile.narrative.title": "Candidate profile",
  "profile.narrative.degraded": "This is a preliminary version. For your fully written profile, create it once more.",
  "profile.facet.sources": { one: "{n} source", other: "{n} sources" },
  "profile.facets.motivations": "Motivations",
  "profile.facets.drives": "Drives",
  "profile.facets.values": "Values",
  "profile.facets.antiValues": "What doesn't fit you",
  "profile.facets.dealBreakers": "Deal-breakers",
  "profile.constraints.title": "Framework conditions",
  "profile.constraints.location": "Location",
  "profile.constraints.salary": "Salary",
  "profile.constraints.workModes": "Work model",

  // ─── Evidence ledger ──────────────────────────────────────────
  "ledger.lead": "We grade every statement we find in your documents. It counts as evidence only once you confirm it.",
  "ledger.filters": "Filter",
  "ledger.filter.all": "All",
  "ledger.status.proposed": "Open",
  "ledger.status.confirmed": "Confirmed",
  "ledger.count.evidenced": "{n} evidenced",
  "ledger.count.selfPositioning": "{n} self-positioning",
  "ledger.count.gap": { one: "{n} gap", other: "{n} gaps" },
  "ledger.count.confirmed": "{n} confirmed",
  "ledger.empty": "No statements yet. Start the analysis in onboarding first.",
  "ledger.emptyFiltered": "No statements match this filter.",

  // ─── CV workbench ─────────────────────────────────────────────
  "cv.lead": "Finalize your CV when you're happy with it. Only finalized versions can be exported.",
  "cv.empty": "No CV yet. Start with onboarding.",
  "cv.current.title": "Current version",
  "cv.draft.title": "Draft",
  "cv.draft.notExportable": "Draft — finalize before exporting",
  "cv.fixVersion": "Finalize version",
  "cv.fixing": "Finalizing …",
  "cv.portrait.add": "Add photo",
  "cv.portrait.replace": "Replace photo",
  "cv.entries.title": "Stations & evidence",
  "cv.entries.claims": { one: "{n} linked statement", other: "{n} linked statements" },
  "cv.entries.noClaims": "no evidence yet",
  "cv.versions.title": "Versions",
  "cv.versions.empty": "No finalized version yet. Finalize the draft to export or print it.",
  "cv.versions.label": "Version {n}",
  "cv.export.md": "Markdown",
  "cv.export.jsonld": "JSON-LD",
  "cv.print": "Print / PDF",
  "cv.print.action": "Print / save as PDF",
  "cv.print.back": "Back to CV",
  "cv.print.loading": "Loading …",
  "cv.print.notFound": "This version does not exist.",

  // ─── Assets inventory ─────────────────────────────────────────
  "asset.lead": "Your certificates, references and portfolio — evidence for your applications.",
  "asset.empty": "No documents yet. Add one, or promote an uploaded document below.",
  "asset.kind.certificate": "Certificate",
  "asset.kind.workCertificate": "Reference letter",
  "asset.kind.reference": "Reference",
  "asset.kind.diploma": "Diploma",
  "asset.kind.portfolio": "Portfolio",
  "asset.kind.portrait": "Photo",
  "asset.kind.other": "Other",
  "asset.confirm": "Confirm",
  "asset.confirmed": "Confirmed",
  "asset.edit": "Edit",
  "asset.save": "Save",
  "asset.remove": "Remove",
  "asset.editTitle": "Edit title",
  "asset.editIssuer": "Edit issuer",
  "asset.issuer": "Issuer",
  "asset.add.group": "Add document",
  "asset.add.kindLabel": "Type",
  "asset.add.upload": "Add document",
  "asset.remove.confirmLabel": "Confirm removal",
  "asset.remove.confirmPrompt": "Really remove?",
  "asset.remove.yes": "Yes, remove",
  "asset.remove.no": "Cancel",
  "asset.promote.title": "Promote uploaded documents",
  "asset.promote.action": "Promote to document",

  // ─── Workshops ────────────────────────────────────────────────
  "workshops.lead": "Find out what really fits you — one sharp question at a time. Three workshops build on each other.",
  "workshops.empty": "No workshops available.",
  "workshop.gate.locked": "locked",
  "workshop.gate.available": "ready",
  "workshop.gate.inProgress": "in progress",
  "workshop.gate.completed": "completed",
  "workshop.progress": "{answered}/{total} steps",
  "workshop.lockedBy": "Complete {name} first",
  "workshop.action.start": "Start",
  "workshop.action.resume": "Resume",
  "workshop.action.revisit": "Revisit",
  "workshop.runner.lead": "Answer only what sharpens the picture. Clear answers, skipping, and later correction are all valid.",
  "workshop.runner.notStarted": "This workshop hasn't started yet.",
  "workshop.runner.notFound.title": "This workshop doesn't exist",
  "workshop.runner.notFound.body": "This link doesn't lead to any of your workshops. Head back to the overview and pick one.",
  "workshop.runner.notFound.back": "To the workshops",
  "workshop.timeline.title": "Your path",
  "workshop.runner.kicker": "An interview about you",
  "workshop.findings.title": "What counts",
  "workshop.findings.lede": "What we infer from your answers. Correct anything that's off — each fix sharpens the match.",
  "workshop.findings.confirmed": "Confirmed",
  "workshop.findings.inferred": "Inferred",
  "workshop.findings.threads": "Open threads",
  "workshop.findings.empty": "Nothing yet — your answers will show up here.",
  "workshop.node.ueberlegung": "Reasoning",
  "workshop.node.skipped": "Skipped — already clear from your documents",
  "workshop.node.optional": "Optional",
  "workshop.node.yourAnswer": "Your answer",
  "workshop.node.produced": { one: "{n} statement", other: "{n} statements" },
  "workshop.node.pending": "Coming up",
  "workshop.card.generate": "Next question",
  "workshop.card.generating": "Thinking …",
  "workshop.card.best": "Strongest",
  "workshop.card.worst": "Most dispensable",
  "workshop.card.choose": "I'd choose",
  "workshop.card.situation": "Situation",
  "workshop.card.unsureLabel": "Unsure?",
  "workshop.card.submit": "Continue",
  "workshop.card.submitting": "Saving …",
  "workshop.card.skipOptional": "Skip and finish workshop",
  "workshop.card.veto": "Off the table",
  "workshop.card.notSure": "It depends",
  "workshop.card.narrativePlaceholder": "In your own words …",
  "workshop.card.unsupported": "This card type isn't supported here yet.",
  "workshop.card.degraded": "This question couldn't be loaded.",
  "workshop.card.degradedSkip": "Skip this question",
  "workshop.consolidate.ready": "The required steps are done. Finish this workshop so the rest of the app can build on it.",
  "workshop.consolidate.action": "Finish workshop",
  "workshop.consolidate.busy": "Finishing …",
  "workshop.context.title": "Result",
  "workshop.context.themes": "Themes",
  "workshop.context.strengths": "Strengths",
  "workshop.context.gaps": "Gaps",
  "workshop.context.criteria": "Criteria",
  "workshop.context.antiValues": "Red lines",
  "workshop.narrative.degraded": "This is a preliminary version. For your fully written summary, generate it once more.",
  "workshop.targeting.title": "Edit search profile",
  "workshop.targeting.hint": "One entry per line.",
  "workshop.targeting.save": "Save",
  "workshop.targeting.roles": "Target roles",
  "workshop.targeting.queries": "Search terms",
  "workshop.targeting.seniority": "Seniority",
  "workshop.targeting.locations": "Locations",
  "workshop.targeting.exclusions": "Exclusions",

  // ─── Search ───────────────────────────────────────────────────
  "search.lead": "Find jobs that fit you. We search, verify and grade — you decide what's worth pursuing.",
  "search.start": "Start search",
  "search.starting": "Starting …",
  "search.stop": "Stop search",
  "search.stopping": "Stopping …",
  "search.config.title": "What we're looking for",
  "search.config.empty": "—",
  "search.targetingHint": "Sharpen your search profile first — the more you tell us, the better the matches.",
  "search.targetingHint.action": "To the search-profile workshop",
  "search.rail.title": "Search run",
  "search.rail.empty": "No run yet. Start a search to watch it work.",
  "search.results.title": "Matching jobs",
  "search.results.empty": "No matches yet. Start a search.",
  "search.results.running": "We're looking for jobs that fit you …",
  "search.bookmarked.title": "Saved",

  // ─── Opportunity (card + detail) ──────────────────────────────
  "opportunity.tier.strong": "Strong fit",
  "opportunity.tier.viable": "Solid fit",
  "opportunity.tier.weak": "Weak fit",
  "opportunity.tier.unsuited": "Not a fit",
  "opportunity.tier.score": "{n}% match",
  "opportunity.stage.discovered": "found",
  "opportunity.stage.verified": "verified",
  "opportunity.stage.briefed": "summarised",
  "opportunity.stage.matched": "graded",
  "opportunity.fit.match": "fits",
  "opportunity.fit.borderline": "borderline",
  "opportunity.fit.mismatch": "doesn't fit",
  "opportunity.jobState.open": "open",
  "opportunity.jobState.closed": "closed",
  "opportunity.jobState.unknown": "status unclear",
  "opportunity.line.verified": "We checked this role — {fit}, {state}.",
  "opportunity.salary": "Salary",
  "opportunity.bookmark": "Save",
  "opportunity.bookmarked": "Saved",
  "opportunity.dismiss": "Hide",
  "opportunity.delete": "Remove",
  "opportunity.open": "Details",
  "opportunity.posting.link": "To the posting",
  "opportunity.brief.title": "At a glance",
  "opportunity.brief.degraded": "This is a preliminary summary. Re-run the search for the fully written version.",
  "opportunity.match.title": "Why this job fits you",
  "opportunity.match.strength": "{n}%",
  "opportunity.match.gaps": "Gaps",
  "opportunity.dimension.fit": "Skills fit",
  "opportunity.dimension.social": "Culture & team",
  "opportunity.dimension.motivational": "Motivation",
  "opportunity.dimension.goal": "Goals & growth",
  "opportunity.detail.notFound": "This job doesn't exist.",
  "opportunity.detail.back": "Back to search",
  "opportunity.detail.removed": "You removed this job.",

  // ─── Organization ─────────────────────────────────────────────
  "org.summary.title": "About the employer",
  "org.summary.empty": "No employer information yet.",
  "org.evidence": { one: "{n} signal", other: "{n} signals" },
  "org.employer.label": "Employer",
  "org.signals.empty": "No citable employer signals yet.",
  "org.source.kununu": "Kununu",
  "org.source.trustpilot": "Trustpilot",
  "org.source.company": "Website",
  "org.source.news": "News",
  "org.source.posting": "Job posting",

  // ─── Profile facet correction (edit) ──────────────────────────
  "profile.edit": "Edit",
  "profile.edit.save": "Save",
  "profile.edit.cancel": "Cancel",
  "profile.identity.throughlineLabel": "Throughline",
  "profile.identity.selfDescriptionLabel": "Self-description",
  "profile.voice.title": "Your voice",
  "profile.voice.samples": "Writing samples (one per line)",
  "profile.voice.empty": "No writing samples yet.",
  "profile.workmode.remote": "Remote",
  "profile.workmode.hybrid": "Hybrid",
  "profile.workmode.onsite": "On-site",

  // ─── Applications (slice 5) ────────────────────────────────────────────────
  "surface.bewerbungen.lead": "Turn a matched job into an application that sounds like you.",
  "applications.list.title": "Your applications",
  "applications.empty": "No applications yet. Pick a job and we'll write one for you.",
  "applications.pick.title": "Which job are you applying for?",
  "applications.pick.placeholder": "Choose a job …",
  "applications.pick.none": "Find and save a job first — then we'll write you an application for it.",
  "applications.generate": "Write my application",
  "applications.generating": "Writing for you …",
  "applications.open": "Open",
  "applications.rail.title": "Your application is taking shape",
  "applications.rail.empty": "Start an application to watch it come together.",
  "applications.stop": "Stop",
  "applications.status.draft": "Draft",
  "applications.status.in_review": "In review",
  "applications.status.changes_requested": "Changes requested",
  "applications.status.approved": "Approved",
  "applications.status.sent": "Sent",
  "applications.gen.stage.preselecting": "Choosing what to say",
  "applications.gen.stage.authoring": "Writing the letter",
  "applications.gen.stage.judging": "Reviewing the letter",
  "applications.gen.stage.kurzprofil": "Writing the short profile",
  "applications.gen.stage.tailoring": "Tailoring the CV",
  "applications.gen.stage.emailing": "Writing the e-mail",
  "applications.gen.stage.done": "Done",
  "applications.gen.status.running": "Running",
  "applications.gen.status.completed": "Completed",
  "applications.gen.status.stopped": "Stopped",
  "applications.gen.status.failed": "Failed",
  "applications.gen.iterations": { one: "{n} review round", other: "{n} review rounds" } as MessageValue,
  "applications.gen.converged": "Passed review",
  "applications.gen.notConverged": "Needs your eye",
  "review.back": "Back to applications",
  "review.notFound": "This application doesn't exist (yet).",
  "review.generating": "Your application is taking shape …",
  "review.letter.title": "Cover letter",
  "review.letter.degraded": "A written draft couldn't be created — this is a plain fallback. Regenerate to try again.",
  "review.kurzprofil.title": "Short profile",
  "review.email.title": "Accompanying e-mail",
  "review.gates.title": "Quality review",
  "review.gate.evidence": "Evidence",
  "review.gate.roleFit": "Role fit",
  "review.gate.motivation": "Motivation",
  "review.gate.clarity": "Clarity",
  "review.gate.antiSlop": "Own voice",
  "review.gate.readerTest": "Readability",
  "review.gate.pass": "OK",
  "review.gate.fail": "Check",
  "review.annotations.title": "Review notes",
  "review.tailoring.title": "Tailored CV",
  "review.tailoring.emphasis": "highlighted",
  "review.attachments.title": "Enclosures",
  "review.smartlinks.title": "Links",
  "review.edit.propose": "Suggest a change",
  "review.edit.save": "Suggest",
  "review.edit.cancel": "Cancel",
  "review.edit.approve": "Apply",
  "review.edit.reject": "Discard",
  "review.edit.pending": "Suggested changes",
  "review.lifecycle.title": "Status",
  "review.lifecycle.toReview": "Send to review",
  "review.lifecycle.approve": "Approve",
  "review.lifecycle.requestChanges": "Request changes",
  "review.lifecycle.markSent": "Mark as sent",
  "review.lifecycle.reopen": "Reopen",
  "review.download": "Download as ZIP",

  // ─── Runs (§8 — the AI-run trace surface) ─────────────────────────────
  "surface.laeufe.lead": "Every AI call, disclosed turn by turn — model, engine, tokens, params, prompt and estimated cost.",

  // ─── LLM settings (§9.1) — per-integration model + params override ───
  "settings.hint": "Override the model and sampling parameters per AI integration. Anything left blank uses the deployed default. Only the fields you set are saved.",
  "settings.loading": "Loading settings…",
  "settings.error": "Error: {message}",
  "settings.retry": "Retry",
  "settings.model": "Model",
  "settings.model.default": "default",
  "settings.temperature": "Temperature",
  "settings.topP": "Top-p",
  "settings.maxTokens": "Max tokens",
  "settings.reasoning": "Reasoning effort",
  "settings.reasoning.none": "Default",
  "settings.reasoning.low": "Low",
  "settings.reasoning.medium": "Medium",
  "settings.reasoning.high": "High",
  "settings.param.default": "Default",
  "settings.current": "Effective: {model}",
  "settings.override": "Overridden",
  "settings.save": "Save",
  "settings.reset": "Reset",
  "settings.saved": "Saved",
  "settings.saveFailed": "Save failed",

  "runs.loading": "Loading runs…",
  "runs.empty": "No AI runs yet. Once you extract documents, run a workshop or a search, every AI call appears here — with prompt, params, tokens and cost.",
  "runs.retry": "Try again",
  "runs.ledger.runs": "Runs",
  "runs.ledger.calls": "AI calls",
  "runs.ledger.tokens": "Tokens",
  "runs.ledger.total": "Total cost",
  "runs.calls": "{n} calls",
  "runs.callsOne": "{n} call",
  "runs.engine.claude": "Claude · Sub",
  "runs.engine.codex": "Codex · Sub",
  "runs.engine.openrouter": "OpenRouter",
  "runs.engine.synthetic": "Deterministic",
  "runs.role.system": "System prompt",
  "runs.role.user": "User message",
  "runs.role.assistant": "Assistant reply",
  "runs.activity.default": "AI run",
  "runs.activity.search": "Job search",
  "runs.activity.ingest": "Document extraction",
  "runs.activity.workshop": "Workshop consolidation",
  "runs.activity.workshopCard": "Workshop · card generation",
  "runs.activity.generate": "Application generation",
  "runs.label.extract": "Extraction",
  "runs.label.verify": "Verification",
  "runs.label.match": "Match",
  "runs.label.orgResearch": "Employer",
  "runs.label.orgSummary": "Employer summary",
  "runs.label.brief": "Brief",
  "runs.label.triage": "Triage",
  "runs.label.preselect": "Preselect",
  "runs.label.author": "Authoring",
  "runs.label.kurzprofil": "Short profile",
  "runs.metric.input": "Input",
  "runs.metric.output": "Output",
  "runs.metric.cache": "Cache hit",
  "runs.metric.latency": "Latency",
  "runs.metric.tokens": "Tokens",
  "runs.metric.yes": "yes",
  "runs.cost.title": "Cost (EUR)",
  "runs.cost.rate": "Rate {rate} · source USD",
  "runs.cost.input": "Input",
  "runs.cost.output": "Output",
  "runs.cost.total": "Total",
  "runs.cost.estimated": "Split estimated by token share (no per-token prices available)",
  "runs.cost.cacheNote": "cache tokens weighted — caching lowers the real cost",
  "runs.cost.free": "Free (subscription engine · marginal cost €0)",
  "runs.cacheChip": "{n} cache tokens",
  "runs.params.title": "Parameters",
  "runs.params.none": "No parameters captured.",
  "runs.prompt.title": "Prompt",
  "runs.prompt.truncatedTag": "shortened",
  "runs.prompt.truncatedNote": "Very large prompts are stored shortened to spare the event store.",
  "runs.prompt.none": "No prompt captured (an earlier run, or a seam without prompt capture).",
  "runs.prompt.copy": "Copy",
  "runs.prompt.copyAll": "Copy all",
  "runs.prompt.copied": "Copied",
  "runs.prompt.copyFailed": "Copy failed",
  "runs.why": "Why this prompt",

  // ─── Credits (Step 5 — buy-credits surface, balance chip, insufficient-balance notice) ───
  "nav.guthaben": "Credits",
  "surface.credits.title": "Credits",
  "credits.lead": "Your credits power your searches and applications. Top up whenever you like.",
  "credits.balance.label": "Your balance",
  "credits.balance.unit": { one: "{n} credit", other: "{n} credits" } as MessageValue,
  "credits.balance.loading": "Loading your balance …",
  "credits.balance.error": "We couldn't load your balance. Please try again.",
  "credits.balance.retry": "Try again",
  "credits.pack.name": "1,000 credits",
  "credits.pack.priceLine": "€10.00 incl. VAT",
  "credits.pack.cta": "Top up balance",
  "credits.pack.starting": "Opening secure checkout …",
  "credits.pack.secure": "You pay securely on Stripe's page — we never see your card details.",
  "credits.unavailable.title": "Top-up isn't available right now",
  "credits.unavailable.body": "Buying credits is briefly unavailable. Please try again in a little while.",
  "credits.checkout.error": "We couldn't start the payment. Please try again.",
  "credits.return.success.title": "Payment received",
  "credits.return.success.body": "Your credits are being added …",
  "credits.return.success.slow": "This is taking a little longer than usual — your credits will be added as soon as the payment is confirmed.",
  "credits.return.success.done": "Your balance has been topped up.",
  "credits.return.cancelled.title": "Purchase cancelled",
  "credits.return.cancelled.body": "No payment was made. You can top up whenever you're ready.",
  "credits.notice.insufficient": "You don't have enough credits for that.",
  "credits.notice.blocked": "Top up your balance to get started.",
  "credits.notice.cta": "Top up balance",
  "credits.notice.dismiss": "Dismiss",
  "credits.chip.label": "Credits",
  "credits.chip.aria": { one: "Balance: {n} credit — top up", other: "Balance: {n} credits — top up" } as MessageValue,
} as const

/** German overrides — the text the user actually reads (de-DE). */
const de: Partial<Record<keyof typeof catalog, MessageValue>> = {
  "app.name": "Career",
  "app.tagline": "Finde Stellen, die zu dir passen. Schreib Bewerbungen, die nach dir klingen.",

  "nav.onboarding": "Onboarding",
  "nav.review": "Vorschläge",
  "nav.workshops": "Workshops",
  "nav.kompass": "Kompass",
  "nav.suche": "Suche",
  "nav.bewerbungen": "Bewerbungen",
  "nav.laeufe": "Läufe",
  "nav.einstellungen": "KI-Einstellungen",
  "nav.herzschlag": "Herzschlag",
  "nav.mehr": "Mehr",
  "nav.profil": "Profil",
  "nav.lebenslauf": "Lebenslauf",
  "nav.belege": "Belege",
  "nav.nachweise": "Nachweise",
  "a11y.skipToContent": "Zum Inhalt springen",

  "auth.login": "Anmelden",
  "auth.logout": "Abmelden",
  "auth.devLogin": "Dev-Anmeldung",
  "auth.signedInAs": "Angemeldet als {name}",

  // ─── App-bar chrome (theme toggle + resource metadata) ────────
  "theme.label": "Farbschema",
  "theme.light": "Hell",
  "theme.dark": "Dunkel",
  "theme.auto": "Auto",
  "bar.nav.label": "Bereiche",
  "bar.resourcePath.label": "Ressourcenpfad",
  "meta.open": "Details zur Ansicht",
  "meta.title": "Diese Ansicht",
  "meta.resource": "Ansicht",
  "meta.owner": "Eigentümer",
  "meta.visibility": "Sichtbarkeit",
  "meta.sharing": "Geteilt mit",
  "meta.visibility.private": "Privat",
  "meta.sharing.none": "Nur du",

  // ─── Top navbar: breadcrumb + user menu (§7a) ─────────────────
  "breadcrumb.label": "Du bist hier",
  "breadcrumb.appSpace": "Bereich wechseln",
  "breadcrumb.step": "Zum Schritt springen",
  "user.menu.open": "Kontomenü",
  "user.menu.title": "Konto",
  "workspace.default": "Standard-Arbeitsbereich",
  "breadcrumb.workshop.self-knowledge": "Workshop 1 · Selbstbild",
  "breadcrumb.workshop.search-profile": "Workshop 2 · Suchprofil",
  "breadcrumb.workshop.profiler": "Workshop 3 · Profiler",

  "login.title": "Bei Career anmelden",
  "login.body": "Career hilft dir, Stellen zu finden, die zu dir passen \u2014 und Bewerbungen zu schreiben, die nach dir klingen. Melde dich mit deinem Platform-Konto an.",
  "login.devHint": "Entwicklungsmodus: Test-Sitzung aktiv (ohne Keycloak).",
  "login.cta": "Mit Platform-Konto anmelden",
  "brand.name": "Career Studio",
  "nav.collapseSidebar": "Seitenleiste einklappen",
  "nav.expandSidebar": "Seitenleiste ausklappen",
  "aside.toggle": "Detailbereich umschalten",
  "aside.title": "Details",
  "aside.empty": "Kontextinfos zur aktuellen Ansicht erscheinen hier.",

  // ─── Linke Leiste: Abschnitte im aktuellen Bereich (§7) ───────
  "sectionnav.label": "Abschnitte",
  "sectionnav.empty": "In diesem Bereich gibt es noch keine Abschnitte.",
  "sectionnav.overview": "Übersicht",
  "sectionnav.ws.self": "Selbstbild",
  "sectionnav.ws.search": "Suchprofil",
  "sectionnav.ws.profiler": "Profiler",
  "sectionnav.onboarding.upload": "Unterlagen",
  "sectionnav.indicator.open": "{n} offen",

  // ─── Rechte Leiste: Platform-KI-Agent ─────────────────────────
  "ai.panel.title": "Platform-KI",
  "ai.panel.subtitle": "Hilfe für alles, was du auf der Platform machst.",
  "ai.panel.toggle": "Platform-KI ein- oder ausblenden",
  "ai.panel.short": "KI",
  "ai.panel.apps": "Deine Anwendungen",
  "ai.panel.thisApp": "Diese App",
  "ai.panel.moreSoon": "Weitere Anwendungen erscheinen hier, sobald du Zugriff darauf hast.",
  "ai.panel.composerPlaceholder": "Frag die Platform-KI …",
  "ai.panel.comingSoon": "Bald verfügbar",

  "notfound.title": "Seite nicht gefunden",
  "notfound.body": "Diese Adresse existiert nicht.",
  "notfound.back": "Zurück zur Suche",

  "common.comingSoon": "Dieser Bereich kommt bald.",
  "gated.hint": "Mit entsprechender Berechtigung verfügbar.",

  "surface.onboarding.title": "Onboarding",
  "surface.extraction.title": "Vorschläge prüfen",
  "surface.workshops.title": "Workshops",
  "surface.workshopRunner.title": "Workshop",
  "surface.kompass.title": "Kompass",
  "surface.profil.title": "Profil",
  "surface.lebenslauf.title": "Lebenslauf",
  "surface.belege.title": "Belege",
  "surface.nachweise.title": "Nachweise",
  "surface.suche.title": "Suche",
  "surface.opportunity.title": "Stelle",
  "surface.bewerbungen.title": "Bewerbungen",
  "surface.laeufe.title": "Läufe",
  "surface.einstellungen.title": "KI-Einstellungen",

  "heartbeat.title": "Herzschlag der Push-Ebene",
  "heartbeat.intro": "Startet einen skriptgesteuerten Suchlauf über die echte API und den echten Datenspeicher und stellt ihn live aus der Push-Ebene dar.",
  "heartbeat.trigger": "Herzschlag auslösen",
  "heartbeat.triggering": "Läuft …",
  "heartbeat.railTitle": "Suchläufe",
  "heartbeat.empty": "Noch keine Suchläufe. Löse einen Herzschlag aus, um die Live-Liste zu sehen.",
  "heartbeat.stage.discovering": "Entdecken",
  "heartbeat.stage.verifying": "Prüfen",
  "heartbeat.stage.matching": "Bewerten",
  "heartbeat.stage.enriching": "Anreichern",
  "heartbeat.stage.done": "Fertig",
  "heartbeat.status.running": "läuft",
  "heartbeat.status.completed": "abgeschlossen",
  "heartbeat.status.stopped": "gestoppt",
  "heartbeat.status.failed": "fehlgeschlagen",
  "heartbeat.count.discovered": "{n} entdeckt",
  "heartbeat.count.verified": "{n} geprüft",
  "heartbeat.count.matched": "{n} bewertet",
  "heartbeat.error": "Fehler: {message}",

  // ─── Onboarding ───────────────────────────────────────────────
  "onboarding.title": "Onboarding",
  "onboarding.lead": "Lade deine Unterlagen hoch. Wir erkennen darin deine Stärken — du entscheidest, was davon zählt.",
  "onboarding.kind.cv": "Lebenslauf",
  "onboarding.kind.letter": "Anschreiben",
  "onboarding.kind.reference": "Arbeitszeugnis",
  "onboarding.kind.certificate": "Zertifikat",
  "onboarding.kind.portrait": "Foto",
  "onboarding.formatHint": "Wir lesen PDF, DOCX, Markdown (.md) und Text (.txt).",
  "onboarding.upload": "Hochladen",
  "onboarding.uploading": "Wird hochgeladen …",
  "onboarding.uploadedN": { one: "{n} Datei hochgeladen", other: "{n} Dateien hochgeladen" },
  "onboarding.error": "Fehler: {message}",
  "onboarding.start": "Auswertung starten",
  "onboarding.starting": "Wird gestartet …",
  "onboarding.runLabel": "Auswertung",
  "onboarding.status.pending": "Bereit",
  "onboarding.status.extracting": "Wird ausgewertet …",
  "onboarding.status.proposed": "Vorschläge liegen vor",
  "onboarding.status.confirmed": "Bestätigt",
  "onboarding.status.failed": "Fehlgeschlagen",
  "onboarding.count.docs": { one: "{n} Dokument", other: "{n} Dokumente" },
  "onboarding.count.claims": { one: "{n} Aussage", other: "{n} Aussagen" },
  "onboarding.runEmpty": "Noch keine Auswertung gestartet.",
  "onboarding.zeroClaims": "Wir konnten keinen auswertbaren Text finden. Gescannte Dokumente oder Bilddateien haben oft keine Textebene — lade nach Möglichkeit eine PDF mit auswählbarem Text hoch.",
  "onboarding.toReview": "Vorschläge prüfen",

  // ─── Extraction review ────────────────────────────────────────
  "review.title": "Vorschläge prüfen",
  "review.lead": "Prüfe, was wir aus deinen Unterlagen gelesen haben. Nichts gilt als belegt, bevor du es bestätigt hast.",
  "review.error": "Fehler: {message}",
  "review.grade.evidenced": "belegt",
  "review.grade.selfPositioning": "Selbsteinschätzung",
  "review.grade.gap": "Lücke",
  "review.sources": { one: "{n} Beleg", other: "{n} Belege" },
  "review.confirm": "Bestätigen",
  "review.confirmed": "Bestätigt",
  "review.edit": "Bearbeiten",
  "review.save": "Speichern",
  "review.discard": "Verwerfen",
  "review.editLabel": "Aussage bearbeiten",
  "review.claims.title": "Aussagen",
  "review.claims.count": "{open} offen · {confirmed} bestätigt",
  "review.claims.empty": "Noch keine Aussagen. Starte zuerst die Auswertung im Onboarding.",
  "review.claims.emptyAfterRun": "Wir konnten keinen auswertbaren Text finden. Gescannte Dokumente oder Bilddateien haben oft keine Textebene — lade nach Möglichkeit eine PDF mit auswählbarem Text hoch.",
  "review.confirmIngest": "Prüfung abschließen",
  "review.rederive": "Erneut auswerten",
  "review.rederiving": "Wird erneut ausgewertet …",
  "review.cv.title": "Lebenslauf-Entwurf",
  "review.cv.name": "Name",
  "review.cv.stations": "Stationen",
  "review.cv.stationsN": { one: "{n} Station", other: "{n} Stationen" },
  "review.cv.skills": "Fähigkeiten",
  "review.cv.skillsN": { one: "{n} Fähigkeit", other: "{n} Fähigkeiten" },
  "review.cv.empty": "Noch kein Lebenslauf-Entwurf.",
  "review.profile.title": "Profil-Ansatz",
  "review.profile.noThroughline": "Noch kein roter Faden erkannt.",
  "review.profile.motives": { one: "{m} Motiv", other: "{m} Motive" },
  "review.profile.values": { one: "{v} Wert", other: "{v} Werte" },
  "review.profile.empty": "Noch kein Profil-Ansatz.",
  "review.narrative.title": "Kandidatenprofil",
  "review.narrative.provenance": { one: "{n} Belegstelle", other: "{n} Belegstellen" },
  "review.narrative.authoredOn": "Erstellt am {date}",
  "review.narrative.stale": "Nicht mehr aktuell — bestätigte Belege haben sich geändert",
  "review.narrative.author": "Kandidatenprofil erstellen",
  "review.narrative.reauthor": "Neu erstellen",
  "review.narrative.authoring": "Wird erstellt …",
  "review.narrative.empty": "Bestätige Belege, dann entsteht hier dein Kandidatenprofil — ein Profil, das nach dir klingt.",

  // ─── Compass hub ──────────────────────────────────────────────
  "compass.lead": "Alles auf einen Blick: wer du bist, was du kannst und wie du es belegst.",
  "compass.profile.desc": "Dein Kandidatenprofil und was dich antreibt.",
  "compass.profile.ready": "bereit",
  "compass.profile.pending": "noch nicht erstellt",
  "compass.cv.desc": "Dein Lebenslauf — Entwurf, Versionen, Export, Druck.",
  "compass.cv.versions": { one: "{n} Version", other: "{n} Versionen" },
  "compass.cv.draft": "Entwurf offen",
  "compass.evidence.desc": "Jede bewertete Aussage, die du belegen kannst.",
  "compass.evidence.confirmed": "{n} bestätigt",
  "compass.evidence.open": "{n} offen",
  "compass.assets.desc": "Deine Zeugnisse, Referenzen und Nachweise.",
  "compass.assets.count": { one: "{n} Nachweis", other: "{n} Nachweise" },

  // ─── Profil ───────────────────────────────────────────────────
  "profile.lead": "So wirkst du — aufgebaut aus deinen bestätigten Belegen.",
  "profile.empty": "Erstelle dein Profil: starte im Onboarding und mach deine Stärken mit Belegen sichtbar.",
  "profile.identity.title": "Dein roter Faden",
  "profile.narrative.title": "Kandidatenprofil",
  "profile.narrative.degraded": "Das ist eine vorläufige Fassung. Erstelle sie noch einmal, um dein ausformuliertes Profil zu bekommen.",
  "profile.facet.sources": { one: "{n} Beleg", other: "{n} Belege" },
  "profile.facets.motivations": "Motive",
  "profile.facets.drives": "Antriebe",
  "profile.facets.values": "Werte",
  "profile.facets.antiValues": "Was nicht zu dir passt",
  "profile.facets.dealBreakers": "No-Gos",
  "profile.constraints.title": "Rahmenbedingungen",
  "profile.constraints.location": "Ort",
  "profile.constraints.salary": "Gehalt",
  "profile.constraints.workModes": "Arbeitsmodell",

  // ─── Belegregister ────────────────────────────────────────────
  "ledger.lead": "Wir bewerten jede Aussage, die wir in deinen Unterlagen finden. Belegt ist sie erst, wenn du sie bestätigst.",
  "ledger.filters": "Filter",
  "ledger.filter.all": "Alle",
  "ledger.status.proposed": "Offen",
  "ledger.status.confirmed": "Bestätigt",
  "ledger.count.evidenced": "{n} belegt",
  "ledger.count.selfPositioning": { one: "{n} Selbsteinschätzung", other: "{n} Selbsteinschätzungen" },
  "ledger.count.gap": { one: "{n} Lücke", other: "{n} Lücken" },
  "ledger.count.confirmed": "{n} bestätigt",
  "ledger.empty": "Noch keine Aussagen. Starte zuerst die Auswertung im Onboarding.",
  "ledger.emptyFiltered": "Keine Aussagen für diesen Filter.",

  // ─── Lebenslauf-Werkbank ──────────────────────────────────────
  "cv.lead": "Finalisiere deinen Lebenslauf, wenn du zufrieden bist. Nur finalisierte Versionen kannst du exportieren.",
  "cv.empty": "Noch kein Lebenslauf. Starte im Onboarding.",
  "cv.current.title": "Aktuelle Version",
  "cv.draft.title": "Entwurf",
  "cv.draft.notExportable": "Entwurf — zum Export erst finalisieren.",
  "cv.fixVersion": "Version finalisieren",
  "cv.fixing": "Wird finalisiert …",
  "cv.portrait.add": "Foto hinzufügen",
  "cv.portrait.replace": "Foto ersetzen",
  "cv.entries.title": "Stationen & Belege",
  "cv.entries.claims": { one: "{n} verknüpfte Aussage", other: "{n} verknüpfte Aussagen" },
  "cv.entries.noClaims": "noch kein Beleg",
  "cv.versions.title": "Versionen",
  "cv.versions.empty": "Noch keine finalisierte Version. Finalisiere den Entwurf, um ihn zu exportieren oder zu drucken.",
  "cv.versions.label": "Version {n}",
  "cv.export.md": "Markdown",
  "cv.export.jsonld": "JSON-LD",
  "cv.print": "Drucken / PDF",
  "cv.print.action": "Drucken / als PDF sichern",
  "cv.print.back": "Zurück zum Lebenslauf",
  "cv.print.loading": "Wird geladen …",
  "cv.print.notFound": "Diese Version existiert nicht.",

  // ─── Nachweise ────────────────────────────────────────────────
  "asset.lead": "Deine Zeugnisse, Referenzen und dein Portfolio — Belege für deine Bewerbungen.",
  "asset.empty": "Noch keine Nachweise. Füge einen hinzu oder übernimm unten ein hochgeladenes Dokument.",
  "asset.kind.certificate": "Zertifikat",
  "asset.kind.workCertificate": "Arbeitszeugnis",
  "asset.kind.reference": "Referenz",
  "asset.kind.diploma": "Diplom",
  "asset.kind.portfolio": "Portfolio",
  "asset.kind.portrait": "Foto",
  "asset.kind.other": "Sonstiges",
  "asset.confirm": "Bestätigen",
  "asset.confirmed": "Bestätigt",
  "asset.edit": "Bearbeiten",
  "asset.save": "Speichern",
  "asset.remove": "Entfernen",
  "asset.editTitle": "Titel bearbeiten",
  "asset.editIssuer": "Aussteller bearbeiten",
  "asset.issuer": "Aussteller",
  "asset.add.group": "Nachweis hinzufügen",
  "asset.add.kindLabel": "Art",
  "asset.add.upload": "Nachweis hinzufügen",
  "asset.remove.confirmLabel": "Entfernen bestätigen",
  "asset.remove.confirmPrompt": "Wirklich entfernen?",
  "asset.remove.yes": "Ja, entfernen",
  "asset.remove.no": "Abbrechen",
  "asset.promote.title": "Hochgeladene Dokumente übernehmen",
  "asset.promote.action": "Als Nachweis übernehmen",

  // ─── Workshops ────────────────────────────────────────────────
  "workshops.lead": "Finde heraus, was wirklich zu dir passt — eine kluge Frage nach der anderen. Drei Workshops bauen aufeinander auf.",
  "workshops.empty": "Keine Workshops verfügbar.",
  "workshop.gate.locked": "gesperrt",
  "workshop.gate.available": "bereit",
  "workshop.gate.inProgress": "läuft",
  "workshop.gate.completed": "abgeschlossen",
  "workshop.progress": "{answered}/{total} Schritte",
  "workshop.lockedBy": "Erst {name} abschließen",
  "workshop.action.start": "Starten",
  "workshop.action.resume": "Fortsetzen",
  "workshop.action.revisit": "Ansehen",
  "workshop.runner.lead": "Beantworte nur, was dein Bild schärfer macht. Du kannst klar antworten, überspringen oder später korrigieren — alles ist gleich gut.",
  "workshop.runner.notStarted": "Dieser Workshop ist noch nicht gestartet.",
  "workshop.runner.notFound.title": "Diesen Workshop gibt es nicht",
  "workshop.runner.notFound.body": "Dieser Link führt zu keinem deiner Workshops. Geh zurück zur Übersicht und wähle einen aus.",
  "workshop.runner.notFound.back": "Zu den Workshops",
  "workshop.timeline.title": "Dein Weg",
  "workshop.runner.kicker": "Ein Interview über dich",
  "workshop.findings.title": "Was zählt",
  "workshop.findings.lede": "Was wir aus deinen Antworten ableiten. Korrigiere, was nicht stimmt — jede Korrektur macht die Treffer genauer.",
  "workshop.findings.confirmed": "Bestätigt",
  "workshop.findings.inferred": "Abgeleitet",
  "workshop.findings.threads": "Offene Fäden",
  "workshop.findings.empty": "Noch nichts — deine Antworten erscheinen hier.",
  "workshop.node.ueberlegung": "Überlegung",
  "workshop.node.skipped": "Übersprungen",
  "workshop.node.optional": "Optional",
  "workshop.node.yourAnswer": "Deine Antwort",
  "workshop.node.produced": { one: "{n} Beleg", other: "{n} Belege" },
  "workshop.node.pending": "Kommt als Nächstes",
  "workshop.card.generate": "Nächste Frage",
  "workshop.card.generating": "Denkt nach …",
  "workshop.card.best": "Am stärksten",
  "workshop.card.worst": "Am ehesten verzichtbar",
  "workshop.card.choose": "Das wähle ich",
  "workshop.card.situation": "Situation",
  "workshop.card.unsureLabel": "Unsicher?",
  "workshop.card.submit": "Weiter",
  "workshop.card.submitting": "Wird gespeichert …",
  "workshop.card.skipOptional": "Überspringen und Workshop abschließen",
  "workshop.card.veto": "Kommt nicht in Frage",
  "workshop.card.notSure": "Kommt drauf an",
  "workshop.card.narrativePlaceholder": "In deinen Worten …",
  "workshop.card.unsupported": "Diese Kartenart wird hier noch nicht unterstützt.",
  "workshop.card.degraded": "Diese Frage konnte nicht geladen werden.",
  "workshop.card.degradedSkip": "Frage überspringen",
  "workshop.consolidate.ready": "Du hast alle nötigen Schritte erledigt. Schließe diesen Workshop ab, damit Suche und weitere Schritte darauf aufbauen können.",
  "workshop.consolidate.action": "Workshop abschließen",
  "workshop.consolidate.busy": "Workshop wird abgeschlossen …",
  "workshop.context.title": "Ergebnis",
  "workshop.context.themes": "Themen",
  "workshop.context.strengths": "Stärken",
  "workshop.context.gaps": "Lücken",
  "workshop.context.criteria": "Kriterien",
  "workshop.context.antiValues": "Rote Linien",
  "workshop.narrative.degraded": "Das ist eine vorläufige Fassung. Erstelle sie noch einmal, um deine ausformulierte Zusammenfassung zu bekommen.",
  "workshop.targeting.title": "Suchprofil bearbeiten",
  "workshop.targeting.hint": "Ein Eintrag pro Zeile.",
  "workshop.targeting.save": "Speichern",
  "workshop.targeting.roles": "Zielrollen",
  "workshop.targeting.queries": "Suchbegriffe",
  "workshop.targeting.seniority": "Erfahrungsstufen",
  "workshop.targeting.locations": "Orte",
  "workshop.targeting.exclusions": "Ausschlüsse",

  // ─── Suche ────────────────────────────────────────────────────
  "search.lead": "Finde Stellen, die zu dir passen. Wir suchen, prüfen und bewerten — du entscheidest, was sich lohnt.",
  "search.start": "Suche starten",
  "search.starting": "Wird gestartet …",
  "search.stop": "Suche stoppen",
  "search.stopping": "Wird gestoppt …",
  "search.config.title": "Wonach wir suchen",
  "search.config.empty": "—",
  "search.targetingHint": "Schärfe zuerst dein Suchprofil — je mehr du uns sagst, desto besser die Treffer.",
  "search.targetingHint.action": "Zum Suchprofil-Workshop",
  "search.rail.title": "Suchlauf",
  "search.rail.empty": "Noch kein Suchlauf. Starte eine Suche und verfolge den Fortschritt.",
  "search.results.title": "Passende Stellen",
  "search.results.empty": "Noch keine Treffer. Starte eine Suche.",
  "search.results.running": "Wir suchen gerade Stellen, die zu dir passen …",
  "search.bookmarked.title": "Gemerkt",

  // ─── Stelle (Karte + Detail) ──────────────────────────────────
  "opportunity.tier.strong": "Starke Passung",
  "opportunity.tier.viable": "Solide Passung",
  "opportunity.tier.weak": "Schwache Passung",
  "opportunity.tier.unsuited": "Unpassend",
  "opportunity.tier.score": "{n}% Passung",
  "opportunity.stage.discovered": "gefunden",
  "opportunity.stage.verified": "geprüft",
  "opportunity.stage.briefed": "aufbereitet",
  "opportunity.stage.matched": "bewertet",
  "opportunity.fit.match": "passt",
  "opportunity.fit.borderline": "nicht eindeutig",
  "opportunity.fit.mismatch": "passt nicht",
  "opportunity.jobState.open": "offen",
  "opportunity.jobState.closed": "geschlossen",
  "opportunity.jobState.unknown": "unbekannt",
  "opportunity.line.verified": "Wir haben die Stelle geprüft: {fit}. Sie ist {state}.",
  "opportunity.salary": "Gehalt",
  "opportunity.bookmark": "Merken",
  "opportunity.bookmarked": "Gemerkt",
  "opportunity.dismiss": "Ausblenden",
  "opportunity.delete": "Entfernen",
  "opportunity.open": "Details",
  "opportunity.posting.link": "Zur Stellenanzeige",
  "opportunity.brief.title": "Kurzüberblick",
  "opportunity.brief.degraded": "Das ist eine vorläufige Zusammenfassung. Starte die Suche erneut, um die ausformulierte Fassung zu bekommen.",
  "opportunity.match.title": "Warum diese Stelle zu dir passt",
  "opportunity.match.strength": "{n}%",
  "opportunity.match.gaps": "Lücken",
  "opportunity.dimension.fit": "Fachliche Passung",
  "opportunity.dimension.social": "Kultur & Team",
  "opportunity.dimension.motivational": "Motivation",
  "opportunity.dimension.goal": "Ziele & Entwicklung",
  "opportunity.detail.notFound": "Diese Stelle gibt es nicht.",
  "opportunity.detail.back": "Zurück zur Suche",
  "opportunity.detail.removed": "Du hast diese Stelle entfernt.",

  // ─── Unternehmen ──────────────────────────────────────────────
  "org.summary.title": "Über das Unternehmen",
  "org.summary.empty": "Noch keine Infos zum Unternehmen.",
  "org.evidence": { one: "{n} Hinweis", other: "{n} Hinweise" },
  "org.employer.label": "Arbeitgeber",
  "org.signals.empty": "Noch keine belegbaren Hinweise zum Arbeitgeber.",
  "org.source.kununu": "Kununu",
  "org.source.trustpilot": "Trustpilot",
  "org.source.company": "Website",
  "org.source.news": "Presse",
  "org.source.posting": "Stellenanzeige",

  // ─── Profil-Korrektur (Bearbeiten) ────────────────────────────
  "profile.edit": "Bearbeiten",
  "profile.edit.save": "Speichern",
  "profile.edit.cancel": "Abbrechen",
  "profile.identity.throughlineLabel": "Roter Faden",
  "profile.identity.selfDescriptionLabel": "Selbstbeschreibung",
  "profile.voice.title": "Deine Stimme",
  "profile.voice.samples": "Schreibproben (eine pro Zeile)",
  "profile.voice.empty": "Noch keine Schreibproben.",
  "profile.workmode.remote": "Remote",
  "profile.workmode.hybrid": "Hybrid",
  "profile.workmode.onsite": "Vor Ort",

  // ─── Applications (slice 5) ────────────────────────────────────────────────
  "surface.bewerbungen.lead": "Aus einer passenden Stelle wird eine Bewerbung, die nach dir klingt.",
  "applications.list.title": "Deine Bewerbungen",
  "applications.empty": "Noch keine Bewerbungen. Wähle eine Stelle aus, dann schreiben wir dir eine Bewerbung dafür.",
  "applications.pick.title": "Für welche Stelle bewirbst du dich?",
  "applications.pick.placeholder": "Stelle auswählen …",
  "applications.pick.none": "Finde und merke dir zuerst eine Stelle — dann schreiben wir dir dafür eine Bewerbung.",
  "applications.generate": "Bewerbung schreiben",
  "applications.generating": "Wir schreiben für dich …",
  "applications.open": "Öffnen",
  "applications.rail.title": "Deine Bewerbung entsteht",
  "applications.rail.empty": "Starte eine Bewerbung und sieh zu, wie sie entsteht.",
  "applications.stop": "Stoppen",
  "applications.status.draft": "Entwurf",
  "applications.status.in_review": "In Prüfung",
  "applications.status.changes_requested": "Änderungen nötig",
  "applications.status.approved": "Freigegeben",
  "applications.status.sent": "Versendet",
  "applications.gen.stage.preselecting": "Schwerpunkte werden gewählt",
  "applications.gen.stage.authoring": "Anschreiben wird geschrieben",
  "applications.gen.stage.judging": "Anschreiben wird geprüft",
  "applications.gen.stage.kurzprofil": "Kurzprofil wird geschrieben",
  "applications.gen.stage.tailoring": "Lebenslauf wird angepasst",
  "applications.gen.stage.emailing": "E-Mail wird geschrieben",
  "applications.gen.stage.done": "Fertig",
  "applications.gen.status.running": "Läuft",
  "applications.gen.status.completed": "Abgeschlossen",
  "applications.gen.status.stopped": "Gestoppt",
  "applications.gen.status.failed": "Fehlgeschlagen",
  "applications.gen.iterations": { one: "{n} Prüfrunde", other: "{n} Prüfrunden" },
  "applications.gen.converged": "Prüfung bestanden",
  "applications.gen.notConverged": "Braucht deinen Blick",
  "review.back": "Zurück zu den Bewerbungen",
  "review.notFound": "Diese Bewerbung gibt es (noch) nicht.",
  "review.generating": "Deine Bewerbung entsteht gerade …",
  "review.letter.title": "Anschreiben",
  "review.letter.degraded": "Es konnte kein ausformulierter Entwurf erstellt werden — das ist eine schlichte Notfassung. Erstelle sie neu, um es noch einmal zu versuchen.",
  "review.kurzprofil.title": "Kurzprofil",
  "review.email.title": "Begleit-E-Mail",
  "review.gates.title": "Qualitätsprüfung",
  "review.gate.evidence": "Belege",
  "review.gate.roleFit": "Passung zur Stelle",
  "review.gate.motivation": "Motivation",
  "review.gate.clarity": "Klarheit",
  "review.gate.antiSlop": "Eigene Sprache",
  "review.gate.readerTest": "Lesbarkeit",
  "review.gate.pass": "OK",
  "review.gate.fail": "Prüfen",
  "review.annotations.title": "Prüfnotizen",
  "review.tailoring.title": "Angepasster Lebenslauf",
  "review.tailoring.emphasis": "hervorgehoben",
  "review.attachments.title": "Anlagen",
  "review.smartlinks.title": "Links",
  "review.edit.propose": "Änderung vorschlagen",
  "review.edit.save": "Vorschlagen",
  "review.edit.cancel": "Abbrechen",
  "review.edit.approve": "Übernehmen",
  "review.edit.reject": "Verwerfen",
  "review.edit.pending": "Vorgeschlagene Änderungen",
  "review.lifecycle.title": "Status",
  "review.lifecycle.toReview": "Zur Prüfung geben",
  "review.lifecycle.approve": "Freigeben",
  "review.lifecycle.requestChanges": "Änderungen erbitten",
  "review.lifecycle.markSent": "Als versendet markieren",
  "review.lifecycle.reopen": "Wieder öffnen",
  "review.download": "Als ZIP herunterladen",

  // ─── KI-Einstellungen (§9.1 — Modell + Parameter je Integration) ──────
  "settings.hint": "Lege je KI-Funktion Modell und Sampling-Parameter fest. Leere Felder nutzen den ausgelieferten Standard; gespeichert werden nur deine Änderungen.",
  "settings.loading": "Einstellungen werden geladen…",
  "settings.error": "Fehler: {message}",
  "settings.retry": "Erneut versuchen",
  "settings.model": "Modell",
  "settings.model.default": "Standard",
  "settings.temperature": "Temperatur",
  "settings.topP": "Top-p",
  "settings.maxTokens": "Max. Tokens",
  "settings.reasoning": "Reasoning-Aufwand",
  "settings.reasoning.none": "Standard",
  "settings.reasoning.low": "Niedrig",
  "settings.reasoning.medium": "Mittel",
  "settings.reasoning.high": "Hoch",
  "settings.param.default": "Standard",
  "settings.current": "Aktuell verwendet: {model}",
  "settings.override": "Überschrieben",
  "settings.save": "Speichern",
  "settings.reset": "Zurücksetzen",
  "settings.saved": "Gespeichert",
  "settings.saveFailed": "Speichern fehlgeschlagen",

  // ─── Läufe (§8 — die KI-Lauf-Spur) ────────────────────────────────────
  "surface.laeufe.lead": "Jeder KI-Schritt, offen einsehbar — mit Modell, Engine, Tokens, Parametern, Prompt und geschätzten Kosten.",
  "runs.loading": "Läufe werden geladen…",
  "runs.empty": "Noch keine KI-Läufe. Sobald du Unterlagen auswertest, einen Workshop machst oder eine Suche startest, erscheint hier jeder KI-Aufruf — mit Prompt, Parametern, Tokens und Kosten.",
  "runs.retry": "Erneut versuchen",
  "runs.ledger.runs": "Läufe",
  "runs.ledger.calls": "KI-Aufrufe",
  "runs.ledger.tokens": "Tokens",
  "runs.ledger.total": "Gesamtkosten",
  "runs.calls": "{n} Aufrufe",
  "runs.callsOne": "{n} Aufruf",
  "runs.engine.claude": "Claude · Abo",
  "runs.engine.codex": "Codex · Abo",
  "runs.engine.openrouter": "OpenRouter",
  "runs.engine.synthetic": "Deterministisch",
  "runs.role.system": "System-Prompt",
  "runs.role.user": "Benutzer-Nachricht",
  "runs.role.assistant": "Assistenz-Antwort",
  "runs.activity.default": "KI-Lauf",
  "runs.activity.search": "Stellensuche",
  "runs.activity.ingest": "Unterlagen auswerten",
  "runs.activity.workshop": "Workshop auswerten",
  "runs.activity.workshopCard": "Workshop · Frage erstellen",
  "runs.activity.generate": "Bewerbungserstellung",
  "runs.label.extract": "Auswertung",
  "runs.label.verify": "Prüfung",
  "runs.label.match": "Passung",
  "runs.label.orgResearch": "Arbeitgeber",
  "runs.label.orgSummary": "Arbeitgeber-Profil",
  "runs.label.brief": "Kurzbericht",
  "runs.label.triage": "Einordnung",
  "runs.label.preselect": "Vorauswahl",
  "runs.label.author": "Verfassen",
  "runs.label.kurzprofil": "Kurzprofil",
  "runs.metric.input": "Eingabe",
  "runs.metric.output": "Antwort",
  "runs.metric.cache": "Cache-Treffer",
  "runs.metric.latency": "Latenz",
  "runs.metric.tokens": "Tokens",
  "runs.metric.yes": "ja",
  "runs.cost.title": "Kosten (EUR)",
  "runs.cost.rate": "Umrechnungskurs {rate} · Grundlage: USD",
  "runs.cost.input": "Eingabe",
  "runs.cost.output": "Antwort",
  "runs.cost.total": "Gesamt",
  "runs.cost.estimated": "Aufteilung nach Token-Anteil geschätzt (keine Preise je Token verfügbar)",
  "runs.cost.cacheNote": "Cache-Tokens werden geringer gewichtet — Cache senkt die realen Kosten.",
  "runs.cost.free": "Kostenfrei · Abo €0",
  "runs.cacheChip": "{n} Cache-Tokens",
  "runs.params.title": "Parameter",
  "runs.params.none": "Keine Parameter erfasst.",
  "runs.prompt.title": "Prompt",
  "runs.prompt.truncatedTag": "gekürzt",
  "runs.prompt.truncatedNote": "Sehr lange Prompts werden gekürzt gespeichert.",
  "runs.prompt.none": "Für diesen Schritt wurde kein Prompt aufgezeichnet.",
  "runs.prompt.copy": "Kopieren",
  "runs.prompt.copyAll": "Alles kopieren",
  "runs.prompt.copied": "Kopiert",
  "runs.prompt.copyFailed": "Kopieren fehlgeschlagen",
  "runs.why": "Warum dieser Prompt",

  // ─── Guthaben (Step 5 — Aufladen, Guthaben-Chip, Hinweis bei zu wenig Guthaben) ───
  "nav.guthaben": "Guthaben",
  "surface.credits.title": "Guthaben",
  "credits.lead": "Mit Guthaben findest du Stellen und schreibst Bewerbungen. Lade auf, wann immer du möchtest.",
  "credits.balance.label": "Dein Guthaben",
  "credits.balance.unit": { one: "{n} Credit", other: "{n} Credits" },
  "credits.balance.loading": "Guthaben wird geladen …",
  "credits.balance.error": "Dein Guthaben konnte nicht geladen werden. Bitte versuch es erneut.",
  "credits.balance.retry": "Erneut versuchen",
  "credits.pack.name": "1.000 Credits",
  "credits.pack.priceLine": "10,00 € inkl. MwSt.",
  "credits.pack.cta": "Guthaben aufladen",
  "credits.pack.starting": "Sichere Bezahlung wird geöffnet …",
  "credits.pack.secure": "Du zahlst sicher auf der Seite von Stripe — deine Kartendaten sehen wir nie.",
  "credits.unavailable.title": "Aufladen ist gerade nicht möglich",
  "credits.unavailable.body": "Der Kauf von Guthaben ist kurz nicht verfügbar. Bitte versuch es gleich noch einmal.",
  "credits.checkout.error": "Die Bezahlung konnte nicht gestartet werden. Bitte versuch es noch einmal.",
  "credits.return.success.title": "Zahlung eingegangen",
  "credits.return.success.body": "Dein Guthaben wird gutgeschrieben …",
  "credits.return.success.slow": "Das dauert gerade etwas länger — dein Guthaben wird gutgeschrieben, sobald die Zahlung bestätigt ist.",
  "credits.return.success.done": "Dein Guthaben wurde aufgeladen.",
  "credits.return.cancelled.title": "Kauf abgebrochen",
  "credits.return.cancelled.body": "Es wurde nichts bezahlt. Du kannst jederzeit aufladen.",
  "credits.notice.insufficient": "Dein Guthaben reicht dafür nicht aus.",
  "credits.notice.blocked": "Lade dein Guthaben auf, um loszulegen.",
  "credits.notice.cta": "Guthaben aufladen",
  "credits.notice.dismiss": "Schließen",
  "credits.chip.label": "Credits",
  "credits.chip.aria": { one: "Guthaben: {n} Credit — aufladen", other: "Guthaben: {n} Credits — aufladen" },
}

export type CareerCatalog = typeof catalog
export type CareerI18n = I18n<CareerCatalog>
export type CareerKey = keyof CareerCatalog

/** Build a scoped i18n instance (German-first). */
export function createCareerI18n(initialLocale = "de-DE"): CareerI18n {
  return createI18n({ catalog, locales: { "de-DE": de }, initialLocale })
}
